/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/
import { useState, useEffect } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Modal from "@mui/material/Modal";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 PRO React components
import MasterCard from "examples/Cards/MasterCard";
import DefaultInfoCard from "examples/Cards/InfoCards/DefaultInfoCard";
import { useMaterialUIController } from "context";
import { serverBaseURL } from "common/settings";
import { useStripe, useElements, Elements, PaymentElement } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

const stripePromise = loadStripe("pk_test_KtVhdGkKsR83L222KaGaDrtT");

// Billing page components
import BaseLayout from "layouts/pages/account/components/BaseLayout";
import BillingInformation from "layouts/pages/account/billing/components/BillingInformation";
import Transactions from "layouts/pages/account/billing/components/Transactions";
import CardInfoForm from "layouts/pages/account/billing/components/CardInfoForm";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";

import masterCardLogo from "assets/images/logos/mastercard.png";
import visaLogo from "assets/images/logos/visa.png";

function Billing() {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { getToken, getUser } = useKindeAuth();
  const [clientSecret, setClientSecret] = useState("");
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupIntentId, setSetupIntentId] = useState("");
  const [cards, setCards] = useState([]);
  const handleSetupOpen = () => {
    getClientSecret();
    setSetupOpen(true);
  };
  const handleSetupClose = () => setSetupOpen(false);

  const options = {
    // passing the SetupIntent's client secret
    clientSecret: clientSecret,
    // Fully customizable with appearance API.
    // appearance: {/*...*/},
  };
  const user = getUser();
  async function getClientSecret() {
    const accessToken = await getToken();
    const customer = await (
      await fetch(`${serverBaseURL}current_customer`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          email: user.email,
        }),
      })
    ).json();
    const setupIntent = await (
      await fetch(`${serverBaseURL}setup_intents/`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          customer_id: customer.id,
        }),
      })
    ).json();
    console.log(setupIntent);
    setSetupIntentId(setupIntent.id);
    setClientSecret(setupIntent.client_secret);
  }

  async function cancelSetupIntent() {
    const accessToken = await getToken();
    console.log("setup intent id:", setupIntentId);
    const setupIntent = await (
      await fetch(`${serverBaseURL}setup_intents/${setupIntentId}/`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          cancellation_reason: "abandoned",
        }),
      })
    ).json();
    console.log(setupIntent);
  }
  async function getSetupIntents() {
    console.log("start get setup intents");
    const accessToken = await getToken();
    const setupIntents = await (
      await fetch(`${serverBaseURL}setup_intents/`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      })
    ).json();
    console.log(setupIntents);
  }

  async function getPaymentMethods() {
    console.log("get payment methods");
    const accessToken = await getToken();
    const customer = await (
      await fetch(`${serverBaseURL}current_customer`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          email: user.email,
        }),
      })
    ).json();
    const baseUrl = `${serverBaseURL}payment_methods/`;
    const params = {
      customer_id: customer.id,
    };
    const url = new URL(baseUrl);
    url.search = new URLSearchParams(params).toString();
    console.log(url);
    const paymentMethods = await (
      await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      })
    ).json();
    console.log(paymentMethods);
    setCards(paymentMethods.data);
  }
  async function deletePaymentMethod(id) {
    const accessToken = await getToken();
    const paymentMethod = await (
      await fetch(`${serverBaseURL}payment_methods/${id}`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      })
    ).json();
    console.log(paymentMethod);
    getPaymentMethods();
  }
  useEffect(() => {
    getPaymentMethods();
  }, []);
  return (
    <BaseLayout stickyNavbar>
      <Modal
        open={setupOpen}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        {clientSecret ? (
          <Elements stripe={stripePromise} options={options}>
            <CardInfoForm
              handleSetupClose={handleSetupClose}
              cancelSetupIntent={cancelSetupIntent}
              getPaymentMethods={getPaymentMethods}
            />
          </Elements>
        ) : (
          <CircularProgress color="inherit" />
        )}
      </Modal>
      <MDBox mt={4}>
        <MDBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card id="delete-account">
                <MDBox
                  pt={2}
                  px={2}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <MDTypography variant="h6" fontWeight="medium">
                    Payment Method
                  </MDTypography>
                  <MDButton variant="gradient" color="dark" onClick={() => handleSetupOpen()}>
                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                    &nbsp;add new card
                  </MDButton>
                </MDBox>
                <MDBox p={2}>
                  <Grid container spacing={3}>
                    {cards.map((item, index) => (
                      <Grid item xs={12} md={12} key={item.id}>
                        <MDBox
                          borderRadius="lg"
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          p={3}
                          sx={{
                            border: ({ borders: { borderWidth, borderColor } }) =>
                              `${borderWidth[1]} solid ${borderColor}`,
                          }}
                        >
                          <MDBox
                            component="img"
                            src={
                              item.card.brand === "mastercard"
                                ? masterCardLogo
                                : item.card.brand === "visa"
                                ? visaLogo
                                : ""
                            }
                            width="10%"
                            mr={2}
                          />
                          <MDTypography variant="h6" fontWeight="medium">
                            ****&nbsp;&nbsp;****&nbsp;&nbsp;****&nbsp;&nbsp;{item.card.last4}
                          </MDTypography>
                          <MDBox
                            ml="auto"
                            lineHeight={0}
                            color={darkMode ? "white" : "dark"}
                            onClick={() => {
                              deletePaymentMethod(item.id);
                            }}
                          >
                            <Tooltip title="Delete Card" placement="top">
                              <Icon sx={{ cursor: "pointer" }} fontSize="small">
                                delete
                              </Icon>
                            </Tooltip>
                          </MDBox>
                        </MDBox>
                      </Grid>
                    ))}
                  </Grid>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
        <MDBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={12}>
              <BillingInformation />
            </Grid>
          </Grid>
        </MDBox>
        {/*}
        <MDBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={12}>
              <Invoices />
            </Grid>
          </Grid>
        </MDBox>
        {*/}
      </MDBox>
    </BaseLayout>
  );
}

export default Billing;
